import { createContext, ReactNode, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { ActivityCenterDto } from '../models';
import { getActivityCenter, getToken } from '../services/api';

interface ActivityCenterContextValue {
  data?: ActivityCenterDto;
  loading: boolean;
  error: string;
  unreadCount: number;
  refresh: () => Promise<void>;
  markAllRead: () => void;
}

const EMPTY: ActivityCenterContextValue = {
  loading: false,
  error: '',
  unreadCount: 0,
  refresh: async () => undefined,
  markAllRead: () => undefined,
};

const ActivityCenterContext = createContext<ActivityCenterContextValue>(EMPTY);
const LAST_READ_KEY = 'telemed_activity_center_last_read_at';

export function ActivityCenterProvider({ children }: { children: ReactNode }) {
  const [data, setData] = useState<ActivityCenterDto>();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [lastReadAt, setLastReadAt] = useState(() => {
    const stored = localStorage.getItem(LAST_READ_KEY);
    if (stored) return stored;
    const initial = new Date().toISOString();
    localStorage.setItem(LAST_READ_KEY, initial);
    return initial;
  });
  const mounted = useRef(true);

  const refresh = useCallback(async () => {
    if (!getToken()) return;
    try {
      const response = await getActivityCenter();
      if (!mounted.current) return;
      setData(response.data.data);
      setError('');
    } catch {
      if (mounted.current) setError('Не удалось обновить центр операций');
    } finally {
      if (mounted.current) setLoading(false);
    }
  }, []);

  useEffect(() => {
    mounted.current = true;
    const onAuthChange = () => {
      if (getToken()) { setLoading(true); refresh(); }
      else setData(undefined);
    };
    onAuthChange();
    const timer = window.setInterval(refresh, 20_000);
    window.addEventListener('telemed-auth-changed', onAuthChange);
    return () => {
      mounted.current = false;
      window.clearInterval(timer);
      window.removeEventListener('telemed-auth-changed', onAuthChange);
    };
  }, [refresh]);

  const markAllRead = useCallback(() => {
    const newest = data?.notifications[0]?.createdAt || new Date().toISOString();
    localStorage.setItem(LAST_READ_KEY, newest);
    setLastReadAt(newest);
  }, [data]);

  const unreadCount = useMemo(() => {
    const boundary = new Date(lastReadAt).getTime();
    return (data?.notifications || []).filter((item) => new Date(item.createdAt).getTime() > boundary).length;
  }, [data, lastReadAt]);

  return <ActivityCenterContext.Provider value={{ data, loading, error, unreadCount, refresh, markAllRead }}>
    {children}
  </ActivityCenterContext.Provider>;
}

export const useActivityCenter = () => useContext(ActivityCenterContext);
